import React, { useMemo, useRef } from 'react'

import type { Deal } from '../types/deal'
import type { Invoice, InvoiceItem } from '../types/invoice'
import { useCompanyOptionsQuery } from '../hooks/useCompanyOptionsQuery'
import { useDealQuery } from '../hooks/useDealQuery'
import { useInvoicesQuery } from '../hooks/useInvoicesQuery'
import { downloadBlob, useInvoicePdfBlob } from '../hooks/useInvoicePdfBlob'
import { useToast } from '../../../hooks/useToast'
import { useModal } from '../state/ModalProvider'
import { formatActivationLabel } from '../utils/dealUrgency'
import { formatCents } from '../utils/formatCents'
import { computeLineTotalCents, computeSubtotalCents } from '../utils/invoiceMath'

interface Props {
  dealId: string
  invoiceId: string
}

function formatTodayYMD(): string {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}/${month}/${day}`
}

function formatYMDFromIso(iso?: string | null): string {
  if (!iso) return formatTodayYMD()
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return formatTodayYMD()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}/${month}/${day}`
}

interface ItemRowProps {
  item: InvoiceItem
  index: number
  invoice: Invoice
}

const ItemRow: React.FunctionComponent<ItemRowProps> = (props) => {
  const { item, index, invoice } = props
  const qty = item.qty || 1
  const rateCents = item.rateCents ?? 0
  const discountPercent = item.discountPercent ?? 0
  const grossCents = rateCents * qty
  const netCents = computeLineTotalCents(item)
  const hasLineDiscount = discountPercent > 0 && netCents < grossCents
  const effectiveRateCents = qty > 0 ? Math.round(netCents / qty) : 0

  const term =
    item.term ||
    (item.isOTP || invoice.addonType === 'onetime' ? 'One-Time' : '1 Year')

  const memoText = item.sub
  const memoParts: React.ReactNode[] = []
  if (memoText) memoParts.push(<>{memoText}</>)
  if (hasLineDiscount && invoice.discountNote && index === 0) {
    memoParts.push(<span className="desc-memo-note">{invoice.discountNote}</span>)
  }

  return (
    <tr>
      <td className="num">{index + 1}</td>
      <td className="desc">
        <div className="desc-title">{item.productName || 'Line item'}</div>
        {memoParts.length > 0 ? (
          <div className="desc-memo">
            {memoParts.map((part, partIndex) => (
              <span key={partIndex}>
                {partIndex > 0 ? ' · ' : null}
                {part}
              </span>
            ))}
          </div>
        ) : null}
      </td>
      <td className="qty">{qty}</td>
      <td className="term">{term}</td>
      <td className="rate">
        {hasLineDiscount ? (
          <>
            <span className="rate-was">{formatCents(rateCents)}</span>
            <span className="rate-now">{formatCents(effectiveRateCents)}</span>
          </>
        ) : (
          formatCents(rateCents)
        )}
      </td>
      <td className="cost">
        {hasLineDiscount ? (
          <>
            <span className="cost-was">{formatCents(grossCents)}</span>
            <span className="cost-now">{formatCents(netCents)}</span>
          </>
        ) : (
          formatCents(netCents)
        )}
      </td>
    </tr>
  )
}

function customerEmailFor(deal: Deal, companyName: string): string {
  if (deal.customerEmail && deal.customerEmail.trim()) return deal.customerEmail.trim()
  const slug = (companyName || deal.companyId).toLowerCase().replace(/\s+/g, '')
  return `billing@${slug}.com`
}

export const InvoicePreviewOverlay: React.FunctionComponent<Props> = (props) => {
  const { dealId, invoiceId } = props
  const { close } = useModal()
  const toast = useToast()
  const dealQuery = useDealQuery(dealId)
  const invoicesQuery = useInvoicesQuery(dealId)
  const pdfMutation = useInvoicePdfBlob()
  const docRef = useRef<HTMLDivElement | null>(null)

  const companyOptionsQuery = useCompanyOptionsQuery()
  const deal = dealQuery.data ?? null
  const invoices = invoicesQuery.data ?? []
  const invoice = useMemo(
    () => invoices.find((entry) => entry.id === invoiceId) ?? null,
    [invoices, invoiceId],
  )
  const companyName = useMemo(() => {
    if (!deal) return ''
    return (
      companyOptionsQuery.data?.find((option) => option.id === deal.companyId)?.name ??
      deal.companyId
    )
  }, [companyOptionsQuery.data, deal])

  if (!deal || !invoice) {
    return (
      <div className="inv-preview-overlay active" onClick={close}>
        <div className="inv-preview-shell" onClick={(event) => event.stopPropagation()}>
          <div className="inv-preview-toolbar">
            <div className="tb-left">
              <span>Invoice Preview</span>
              <span className="tb-badge">PDF</span>
            </div>
            <div className="tb-actions">
              <button
                type="button"
                className="btn-close"
                onClick={close}
                title="Close"
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
          <div className="inv-preview-content" style={{ padding: 32 }}>
            Loading invoice…
          </div>
        </div>
      </div>
    )
  }

  const items: InvoiceItem[] = invoice.items ?? []
  const subtotalCents = invoice.subtotalCents ?? computeSubtotalCents(items)
  const feeRecoveryCents = invoice.feeRecoveryCents ?? 0
  const discountCents = invoice.discountCents ?? 0
  const netItemCents = computeSubtotalCents(items)
  const hasLineDiscounts = items.some((item) => (item.discountPercent ?? 0) > 0)
  const totalCents = hasLineDiscounts
    ? netItemCents + feeRecoveryCents
    : invoice.totalCents ?? subtotalCents + feeRecoveryCents - discountCents

  const createdDate = formatYMDFromIso(invoice.createdTime)
  const poNumber = invoice.poNumber ?? ''
  const custEmail = customerEmailFor(deal, companyName)
  const sentMeta = invoice.sentMeta

  const invoiceNumber = invoice.invoiceNumber
  function handleDownload(): void {
    pdfMutation.mutate(
      { dealId, invoiceId, source: docRef.current },
      {
        onSuccess: (blob) => {
          const safeName = invoiceNumber.replace(/[^A-Za-z0-9_.-]/g, '_')
          downloadBlob(blob, `${safeName}.pdf`)
          toast.current?.show({
            severity: 'success',
            summary: 'PDF ready',
            detail: `${invoiceNumber}.pdf downloaded.`,
            life: 2500,
          })
        },
      },
    )
  }

  return (
    <div className="inv-preview-overlay active" onClick={close}>
      <div className="inv-preview-shell" onClick={(event) => event.stopPropagation()}>
        <div className="inv-preview-toolbar">
          <div className="tb-left">
            <span>Invoice Preview</span>
            <span className="tb-badge">PDF</span>
          </div>
          <div className="tb-actions">
            <button
              type="button"
              className="btn-download"
              title="Download PDF"
              onClick={handleDownload}
              disabled={pdfMutation.isLoading}
            >
              <svg
                viewBox="0 0 24 24"
                width={14}
                height={14}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1={12} y1={15} x2={12} y2={3} />
              </svg>
              {pdfMutation.isLoading ? 'Preparing…' : 'Download PDF'}
            </button>
            <button
              type="button"
              className="btn-close"
              onClick={close}
              title="Close"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="inv-preview-content">
          {sentMeta ? (
            <div className="inv-sent-banner">
              <svg
                viewBox="0 0 24 24"
                width={14}
                height={14}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 2 11 13" />
                <path d="M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
              {' '}Sent to <strong>{sentMeta.to}</strong> · {sentMeta.at}
            </div>
          ) : null}

          <div className="inv-doc" id="invDocPrintable" ref={docRef}>
            <div className="inv-doc-header">
              <img src="/calyx-logo.png" alt="Calyx" className="inv-doc-logo" />
              <div className="inv-doc-company">
                <div className="co-name">Calyx, Inc.</div>
                <div className="co-addr">
                  450 Sutardja Dai Hall, UC Berkeley, Berkeley CA 94720-1764
                </div>
                <div className="co-web">www.calyxtechs.com</div>
              </div>
            </div>

            <div className="inv-doc-title">
              <h1>Commercial Invoice</h1>
              <div className="ship-to-label">SHIP TO:</div>
            </div>

            <div className="inv-doc-meta">
              <div className="meta-col">
                <div className="meta-row">
                  <span className="meta-k">Invoice No.</span>
                  <span className="meta-v mono">{invoice.invoiceNumber}</span>
                </div>
                <div className="meta-row">
                  <span className="meta-k">Created Date</span>
                  <span className="meta-v">{createdDate}</span>
                </div>
                {poNumber ? (
                  <div className="meta-row">
                    <span className="meta-k">Refer to PO No.</span>
                    <span className="meta-v mono">{poNumber}</span>
                  </div>
                ) : null}
              </div>
              <div className="meta-col">
                <div className="meta-row">
                  <span className="meta-k">Prepared For</span>
                  <span className="meta-v">{deal.salesRepInitials || 'Calyx Sales'}</span>
                </div>
                <div className="meta-row">
                  <span className="meta-k">Company Name</span>
                  <span className="meta-v">
                    <strong>{companyName}</strong>
                  </span>
                </div>
                <div className="meta-row">
                  <span className="meta-k">Shipping Address</span>
                  <span className="meta-v">Shipping address on file</span>
                </div>
                <div className="meta-row">
                  <span className="meta-k">Email</span>
                  <span className="meta-v">{custEmail}</span>
                </div>
              </div>
            </div>

            <div className="inv-doc-section-title">PRICE DETAILS</div>
            <table className="inv-doc-table">
              <thead>
                <tr>
                  <th className="num">Line</th>
                  <th className="desc">Description</th>
                  <th className="qty">Qty.</th>
                  <th className="term">Term</th>
                  <th className="rate">Rate</th>
                  <th className="cost">Cost</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map((item, index) => (
                    <ItemRow key={index} item={item} index={index} invoice={invoice} />
                  ))
                ) : (
                  <tr>
                    <td className="num">1</td>
                    <td className="desc">
                      <div className="desc-title">
                        Subscription services — see attached line items
                      </div>
                    </td>
                    <td className="qty">1</td>
                    <td className="term">1 Year</td>
                    <td className="rate">—</td>
                    <td className="cost">{formatCents(totalCents)}</td>
                  </tr>
                )}
                {feeRecoveryCents > 0 ? (
                  <tr className="adj">
                    <td colSpan={5} className="adj-label">
                      Fee recovery
                      {invoice.feeRecoveryNote ? (
                        <>
                          {' '}
                          <span className="adj-note">({invoice.feeRecoveryNote})</span>
                        </>
                      ) : null}
                    </td>
                    <td className="cost">+{formatCents(feeRecoveryCents)}</td>
                  </tr>
                ) : null}
                {discountCents > 0 && !hasLineDiscounts ? (
                  <tr className="adj">
                    <td colSpan={5} className="adj-label">
                      Discount
                      {invoice.discountNote ? (
                        <>
                          {' '}
                          <span className="adj-note">({invoice.discountNote})</span>
                        </>
                      ) : null}
                    </td>
                    <td className="cost">−{formatCents(discountCents)}</td>
                  </tr>
                ) : null}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} className="total-label">
                    TOTAL DUE (USD)
                  </td>
                  <td className="total-val">{formatCents(totalCents)}</td>
                </tr>
              </tfoot>
            </table>

            <div className="inv-doc-terms">
              <div className="terms-col">
                <div className="terms-k">Default / Expected Lead Time</div>
                <div className="terms-v">{formatActivationLabel(deal.activationDate) || 'Upon PO confirmation'}</div>
              </div>
              <div className="terms-col">
                <div className="terms-k">Payment Term</div>
                <div className="terms-v">NET 15 Upon Invoice Issue Date</div>
              </div>
              <div className="terms-col">
                <div className="terms-k">Shipping Term</div>
                <div className="terms-v">DDP</div>
              </div>
            </div>

            <div className="inv-doc-footer">
              <div className="footer-col">
                <div className="footer-title">Calyx Contact</div>
                <div className="footer-body">
                  <div>
                    <strong>{deal.salesRepInitials || 'JD Chang'}</strong>
                  </div>
                  <div>Calyx Biotechs, Inc.</div>
                  <div>Rm. 208, 2F., Incubation Center Bldg. B,</div>
                  <div>No.18, Siyuan St., Zhongzheng Dist.,</div>
                  <div>Taipei City 10087, Taiwan (R.O.C.)</div>
                  <div>+886 2-2365-8520</div>
                  <div className="mono">jdchang@calyxtechs.com</div>
                </div>
              </div>
              <div className="footer-col">
                <div className="footer-title">Wire transfer instruction</div>
                <div className="footer-body">
                  <div>
                    <span className="lbl">Bank:</span> JPMorgan Chase Bank NA
                  </div>
                  <div>
                    <span className="lbl">Bank Address:</span> 270 Park Avenue, New York, NY 10017
                  </div>
                  <div>
                    <span className="lbl">Routing Number:</span>{' '}
                    <span className="mono">322271627</span>
                  </div>
                  <div>
                    <span className="lbl">Account Number:</span>{' '}
                    <span className="mono">201702021</span>
                  </div>
                  <div>
                    <span className="lbl">SWIFT Code:</span>{' '}
                    <span className="mono">CHASUS33</span>
                  </div>
                  <div>
                    <span className="lbl">Payee Name:</span> Calyx, Inc.
                  </div>
                  <div>
                    <span className="lbl">Payee Address:</span> 2547 Chinook Dr., Walnut Creek, CA 94598
                  </div>
                  <div>
                    <span className="lbl">Contact:</span> 510-502-4560
                  </div>
                </div>
              </div>
            </div>

            <div className="inv-doc-page-foot">
              <span>https://calyxtechs.com</span>
              <span>-- 1 of 1 --</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
